import {ReactNode} from "react";
import {Box, IconButton, Paper, Tooltip, Typography} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const WIDTH = 340;
const STRIP_WIDTH = 28;

// text written down the folded strip.
export function StripLabel(props: {children: ReactNode; color?: string}) {
    return (
        <Typography variant="caption" color={props.color ?? "text.secondary"} sx={{
            writingMode: "vertical-rl",
            mt: 1,
            userSelect: "none",
        }}>
            {props.children}
        </Typography>
    );
}

// the shell of a workspace's right sidebar: a header with a title line and a fold
// button over a scrolling body. Folded, it is a thin strip with the name written
// down it and an unfold button. The caller owns the collapsed state.
export function Sidebar(props: {
    // the short name on the folded strip and in the fold/unfold tooltips.
    name: string;
    // what the folded strip shows under the unfold button, in place of the name.
    strip?: ReactNode;
    title: ReactNode;
    subtitle?: ReactNode;
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
    children: ReactNode;
}) {
    if(props.collapsed) {
        return (
            <Paper square elevation={0} sx={{
                width: STRIP_WIDTH,
                flexShrink: 0,
                borderLeft: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: 0.5,
            }}>
                <Tooltip title={`Show ${props.name.toLowerCase()}`} placement="left">
                    <IconButton size="small" onClick={() => props.onCollapsedChange(false)}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {props.strip ?? <StripLabel>{props.name}</StripLabel>}
            </Paper>
        );
    }

    return (
        <Paper square elevation={0} sx={{
            width: WIDTH,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
        }}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider"}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="subtitle2" noWrap>
                        {props.title}
                    </Typography>
                    {props.subtitle !== undefined && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {props.subtitle}
                        </Typography>
                    )}
                </Box>
                <Tooltip title={`Hide ${props.name.toLowerCase()}`} placement="left">
                    <IconButton size="small" onClick={() => props.onCollapsedChange(true)}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{flex: 1, minHeight: 0, overflow: "auto"}}>
                {props.children}
            </Box>
        </Paper>
    );
}
